import ClassicExtension from "./ClassicExtension";

const PARTICLE_COUNT = 20;
const COLORS = ['#f94144', '#f3722c', '#f8961e', '#f9c74f', '#90be6d', '#43aa8b', '#577590'];

// Overall pace of the whole simulation (velocities and angular speed); 1 = full speed.
const SPEED_SCALE = 0.75;

const SIMULATION_STEP = 1 / 30; // seconds between simulated trajectory samples
const MAX_SIMULATION_TIME = 8; // safety cap in case a particle never leaves the viewport
const EXIT_MARGIN = 40; // px past the viewport edge before a particle counts as gone

interface TrajectorySample {
    t: number;
    x: number;
    y: number;
    rotation: number;
}

function randomInRange(min: number, max: number): number {
    return min + Math.random() * (max - min);
}

/**
 * Fires a small burst of confetti from the cursor position when an element with [data-confetti] is hovered.
 * Plays at most once per element per page load and is skipped for prefers-reduced-motion users.
 *
 * Each particle is launched with an initial velocity and, under simulated air resistance, its velocity
 * exponentially relaxes towards a slow terminal value on each axis (a gentle fall speed vertically, a small
 * residual drift horizontally) rather than following a fixed path. It disappears once it exits the viewport.
 */
export default class ConfettiExtension extends ClassicExtension {

    protected init($root: JQuery) {
        $root.find("[data-confetti]").not('.event-binded').on('mouseenter', e => {
            const $target = $(e.currentTarget);
            if ($target.hasClass('confetti-played')) {
                return;
            }
            $target.addClass('confetti-played');

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return;
            }

            ConfettiExtension.burst(e.clientX, e.clientY);
        }).addClass('event-binded');
    }

    private static burst(x: number, y: number): void {
        for (let i = 0; i < PARTICLE_COUNT; i++) {
            ConfettiExtension.spawnParticle(x, y);
        }
    }

    private static spawnParticle(originX: number, originY: number): void {
        const particle = document.createElement('span');
        particle.className = 'confetti-particle';
        particle.style.left = `${originX}px`;
        particle.style.top = `${originY}px`;
        particle.style.backgroundColor = COLORS[Math.floor(Math.random() * COLORS.length)];

        const size = 6 + Math.random() * 6;
        particle.style.width = `${size}px`;
        particle.style.height = `${size * 0.4}px`;

        document.body.appendChild(particle);

        const trajectory = ConfettiExtension.simulateTrajectory(originX, originY);
        const totalDuration = trajectory[trajectory.length - 1].t;

        const animation = particle.animate(
            trajectory.map(sample => ({
                transform: `translate(${sample.x}px, ${sample.y}px) rotate(${sample.rotation}deg)`,
                offset: sample.t / totalDuration,
            })),
            {
                duration: totalDuration * 1000,
                easing: 'linear',
            },
        );

        animation.onfinish = () => particle.remove();
    }

    /**
     * Simulates flight under linear air resistance: velocity on each axis exponentially relaxes from its
     * initial value v0 towards a terminal value vTerm with time constant tau, i.e.
     * v(t) = vTerm + (v0 - vTerm) * e^(-t/tau), integrated to a position. Samples run from launch until the
     * particle crosses the viewport edge (or a safety time cap is hit).
     */
    private static simulateTrajectory(originX: number, originY: number): TrajectorySample[] {
        const vx0 = randomInRange(-200, 200) * SPEED_SCALE;
        const vy0 = randomInRange(-450, -300) * SPEED_SCALE; // negative = launched upward
        const vxTerm = Math.sign(vx0 || 1) * randomInRange(20, 50) * SPEED_SCALE; // residual sideways drift
        const vyTerm = randomInRange(300, 450) * SPEED_SCALE; // downward fall speed
        const tau = randomInRange(0.4, 0.7); // drag time constant, seconds
        const angularVelocity = (Math.random() < 0.5 ? -1 : 1) * randomInRange(120, 480) * SPEED_SCALE;

        const positionAt = (v0: number, vTerm: number, t: number): number =>
            vTerm * t + (v0 - vTerm) * tau * (1 - Math.exp(-t / tau));

        const samples: TrajectorySample[] = [{ t: 0, x: 0, y: 0, rotation: 0 }];

        for (let t = SIMULATION_STEP; t <= MAX_SIMULATION_TIME; t += SIMULATION_STEP) {
            const x = positionAt(vx0, vxTerm, t);
            const y = positionAt(vy0, vyTerm, t);
            samples.push({ t, x, y, rotation: angularVelocity * t });

            const exitedBottom = originY + y > window.innerHeight + EXIT_MARGIN;
            const exitedSide = originX + x < -EXIT_MARGIN || originX + x > window.innerWidth + EXIT_MARGIN;
            if (exitedBottom || exitedSide) {
                break;
            }
        }

        return samples;
    }

}
